import type { StackProps } from '@chakra-ui/react'
import { Stack } from '@chakra-ui/react'
import { useViewport } from '@xyflow/react'
import type { FC } from 'react'
import { memo } from 'react'

import { Card, CardRollupType, CardSource } from '../components'
import CardLabels from '../components/cardLabels'

import CardMetricGoals from './cardMetricGoals'
import CardRegressionScore from './cardRegressionScore'
import CardStats from './cardStats'

import useGetObjectProperty from '@app/hooks/useGetObjectProperty'
import useGetObjectsByProperties from '@app/hooks/useGetObjectsByProperties'
import useIsAnonymousUser from '@app/hooks/useIsAnonymousUser'
import MetricTypePopover from '@app/next/metricTypePopover'
import CollapsedNodes from '@app/pages/strategies/components/nodes/components/collapsedNodes'
import { scaleSizeToMapZoom } from '@app/pages/strategies/components/nodes/helpers'
import SimMetricInputs from '@app/pages/strategies/components/nodes/simulator/simMetricInputs'
import SimOnePercentPartialsSection from '@app/pages/strategies/components/nodes/simulator/simOnePercentPartialsSection'
import SimPartialsSection from '@app/pages/strategies/components/nodes/simulator/simPartialsSection'
import cardMetadata, { type CardMetadata } from '@app/shared/cards/cardMetadata'
import CardDetailsLink from '@app/shared/cards/components/cardDetailsLink'
import CardFormula from '@app/shared/cards/components/cardFormula'
import CardGoalsCTA from '@app/shared/cards/components/cardGoalsCTA'
import CardSection from '@app/shared/cards/components/cardSection'
import CardSharedActions from '@app/shared/cards/components/cardSharedActions'
import CardTitle from '@app/shared/cards/components/cardTitle'
import CardTypeSection from '@app/shared/cards/components/cardTypeSection'
import CardMetricSourceCTA from '@app/shared/cards/metric/cardMetricSourceCTA'
import MarkdownDisplay from '@app/shared/markdownDisplay'
import PulsingCircle from '@app/shared/pulsingCircle'
import type { DomainMetric, DomainNode, MapMode } from '@app/types'
import ICON_MAP from '@app/utils/iconMap'
import { GoalStatusEnum } from '@graphql/types'

interface Props extends StackProps {
  strategyId: string
  metric?: DomainMetric
  node?: DomainNode
  mapMode?: MapMode
}

const MetricCard: FC<Props> = ({ strategyId, metric, node, mapMode = 'build', ...rest }) => {
  const isAnonymousUser = useIsAnonymousUser(strategyId)
  const { size, color, regressionScore, regressionScoreType }: CardMetadata = cardMetadata(node)
  const simulationMode = mapMode === 'simulation'

  const showCorrelations = useGetObjectProperty(strategyId, 'strategy', 'showCorrelations')
  const goals = useGetObjectsByProperties('goal', { metricId: metric.id })
  const metricSources = useGetObjectsByProperties('metricSource', { metricId: metric.id })
  const metricSource = metricSources?.[0]

  const { zoom } = useViewport()

  const {
    calculated,
    description,
    externalUrl,
    externalUrlTitle,
    id,
    labels,
    metricDataPointsCount,
    recentActivity,
    showDescription,
    showTechnicalDescription,
    sourceName,
    technicalDescription,
    slug
  } = metric

  const showStats = node?.strategyStats?.some((stat) => stat.total || stat.change) || false
  const goalIds = goals ? goals.filter((goal) => goal.status === GoalStatusEnum.Current).map((goal) => goal.id) : []
  const route = isAnonymousUser ? null : `/strategy/${strategyId}/map/metric/${metric.id}`

  const pulseSize = scaleSizeToMapZoom(24, zoom, 44)

  const boxProps: StackProps = {}

  const showGoals = goalIds.length > 0 && !simulationMode

  const showGoalsCTA = !isAnonymousUser && !goalIds.length && mapMode === 'goals'
  const showDataCTA = !isAnonymousUser && !metricDataPointsCount && mapMode === 'data'

  return (
    <Card color={color} {...boxProps} {...rest} data-cy="metric-card">
      <CardTypeSection title="Metric /" icon={ICON_MAP.Metric} color={color} route={route} size={size} nodeId={slug}>
        <MetricTypePopover metric={metric} fontWeight="semibold" size={size} color={color} />
      </CardTypeSection>
      <CardSection size={size}>
        <PulsingCircle isActive={!!recentActivity} size={pulseSize} link={`metric/${id}/events`} />
        <Stack>
          <CardTitle domainObject={metric} name="name" size={size} />
          {showDescription && description && <MarkdownDisplay text={description} />}
          {showTechnicalDescription && technicalDescription && <MarkdownDisplay text={technicalDescription} />}
          {externalUrl && <CardDetailsLink url={externalUrl} text={externalUrlTitle} size={size} />}
          {showStats && !simulationMode && (
            <CardStats strategyId={strategyId} metric={metric} node={node} size={size} />
          )}
        </Stack>
      </CardSection>
      {calculated && <CardFormula metricSource={metricSource} />}
      {showDataCTA && <CardMetricSourceCTA id={id} size={size} />}
      {showGoals && (
        <CardSection size={size}>
          <CardMetricGoals metric={metric} goalIds={goalIds} size={size} />
        </CardSection>
      )}
      {showGoalsCTA && <CardGoalsCTA link={`metric/${id}/key_results`} size={size} title="Set key result" />}
      {labels?.length && <CardLabels fieldName="labels" domainObject={metric} size={size} />}
      <CardSharedActions domainObject={metric} size={size} route={route} includeConfidenceRating={false}>
        <CardSource sourceName={sourceName} metricId={id} metricSource={metricSource} size={size} color="fg.subtle" />
        {showStats && <CardRollupType metric={metric} size={size} />}
      </CardSharedActions>
      {node && <CollapsedNodes node={node} />}
      {showCorrelations && (
        <CardRegressionScore regressionScore={regressionScore} regressionScoreType={regressionScoreType} size={size} />
      )}
      {simulationMode && <SimMetricInputs node={node} size={size} />}
      {simulationMode && <SimPartialsSection node={node} size={size} />}
      {simulationMode && !metric.calculated && <SimOnePercentPartialsSection node={node} size={size} />}
    </Card>
  )
}

export default memo(MetricCard)
